import axios, { AxiosInstance, AxiosResponse } from 'axios'
import { logger } from './logger'
import { withApiFallback } from '../shared/api/withApiFallback'
import type {
  UserAuthResponse,
  SubscriptionResponse,
  CreateSubscriptionRequest,
  CreateSubscriptionResponse,
  DeleteSubscriptionResponse,
  CategoriesResponse,
  CardsResponse,
  AnnouncementsResponse,
  CardWithContentResponse,
  FavoritesResponse,
  FavoriteStatusResponse,
  UsersCountResponse,
  AddToFavoritesRequest,
  RemoveFromFavoritesRequest,
  SubscriptionPriceListResponse,
  CheckSpinResponse,
  PrizesResponse,
  MyPrizesResponse,
  SpinResponse,
  CoursesResponse,
  CourseDetailResponse,
  LessonDetailResponse,
} from '../types/api'

/**
 * Определяет, нужно ли логировать API ошибку
 */
export function shouldLogApiError(
  status: number,
  url: string,
  responseData: any
): boolean {
  const detail = responseData?.detail

  // Не логируем ошибки, связанные с отсутствием активной подписки
  if (
    detail &&
    (detail.includes('Активная подписка не найдена') ||
      detail.includes('подписка не найдена') ||
      detail.includes('subscription not found') ||
      detail.includes('Для просмотра избранного нужна активная подписка') ||
      detail.includes('Для доступа к категориям требуется активная подписка') ||
      detail.includes('активная подписка') ||
      detail.includes('требуется подписка') ||
      detail.includes('нужна подписка'))
  ) {
    return false
  }

  // Не логируем ошибки отсутствия подписки
  if (status === 404 && url?.includes('/subscription')) {
    if (
      detail &&
      (detail.includes('подписка не найдена') ||
        detail.includes('subscription not found') ||
        detail.includes('Активная подписка не найдена'))
    ) {
      return false
    }
  }

  // Не логируем ошибки отсутствия избранного (если пользователь еще не добавлял ничего)
  if (status === 404 && url?.includes('/favorites')) {
    if (
      detail &&
      (detail.includes('избранное не найдено') ||
        detail.includes('favorites not found'))
    ) {
      return false
    }
  }

  // Не логируем ошибки отсутствия контента (404 на карточки/категории)
  if (
    status === 404 &&
    (url?.includes('/card/') || url?.includes('/categories'))
  ) {
    return false
  }

  // Логируем все остальные ошибки
  return true
}

class ApiClient {
  private client: AxiosInstance
  private telegramInitData: string | null = null

  constructor() {
    // Всегда используем HTTPS для избежания редиректов 301

    const baseURL = (import.meta as any).env?.VITE_API_BASE_URL

    this.client = axios.create({
      baseURL,
      timeout: 30000,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
      // Не следуем редиректам автоматически для лучшего контроля
      maxRedirects: 0,
    })

    // Переключение на запасной бэкенд — первым перехватчиком, до логирования
    // ошибок: повтор должен успеть отработать раньше, чем мы сообщим о сбое.
    withApiFallback(this.client)

    // Добавляем перехватчик для логирования запросов
    this.client.interceptors.request.use(config => {
      // Добавляем заголовок Init-Data если он есть
      if (this.telegramInitData) {
        config.headers['Init-Data'] = this.telegramInitData
      } else {
        console.warn('⚠️ Отправляем запрос БЕЗ заголовка Init-Data')
      }

      return config
    })

    // Перехватчик ответов для обработки ошибок
    this.client.interceptors.response.use(
      response => {
        return response
      },
      async error => {
        const status = error.response?.status
        const method = error.config?.method?.toUpperCase()
        const url = error.config?.url

        console.error(
          `❌ API Error: ${status} ${method} ${url}`,
          error.response?.data
        )

        // Проверяем, нужно ли логировать эту ошибку
        const shouldLog = shouldLogApiError(status, url, error.response?.data)

        if (shouldLog) {
          // Логируем ошибку на сервер
          await logger.logError(error, {
            method,
            url,
            errorType: 'api_error',
            additionalData: {
              status,
              response_data: error.response?.data,
              request_data: error.config?.data,
              detail: error.response?.data?.detail, // Добавляем detail из ответа
            },
          })
        }

        if (status === 301 || status === 302) {
          console.warn(
            '🔄 Редирект обнаружен! Проверьте базовый URL (HTTP vs HTTPS)'
          )
        } else if (status === 401) {
          console.warn('🔒 Пользователь не авторизован')
        } else if (status === 403) {
          console.warn('🚫 Нет доступа к контенту')
        }
        return Promise.reject(error)
      }
    )
  }

  // Установить данные Telegram для авторизации
  setTelegramInitData(initData: string) {
    this.telegramInitData = initData
  }

  // Методы API

  // === Пользователь ===
  async auth(
    telegramUserId: number,
    userId: number = 0
  ): Promise<UserAuthResponse> {
    const authData = {
      user: {
        telegram_user_id: telegramUserId,
        user_id: userId,
      },
      is_new_user: false,
    }

    const response: AxiosResponse<UserAuthResponse> = await this.client.post(
      '/api/user/auth',
      authData
    )
    return response.data
  }

  async getSubscription(): Promise<SubscriptionResponse> {
    const response: AxiosResponse<SubscriptionResponse> = await this.client.get(
      '/api/user/subscription'
    )
    return response.data
  }

  async createSubscription(
    request: CreateSubscriptionRequest
  ): Promise<CreateSubscriptionResponse> {
    const response: AxiosResponse<CreateSubscriptionResponse> =
      await this.client.post('/api/user/subscription/create', request)
    return response.data
  }

  async deleteSubscription(): Promise<DeleteSubscriptionResponse> {
    const response: AxiosResponse<DeleteSubscriptionResponse> =
      await this.client.post('/api/user/subscription/delete')
    return response.data
  }

  async getFavorites(): Promise<FavoritesResponse> {
    const response: AxiosResponse<FavoritesResponse> = await this.client.get(
      '/api/user/favorites'
    )
    return response.data
  }

  async addToFavorites(
    request: AddToFavoritesRequest
  ): Promise<FavoriteStatusResponse> {
    const response: AxiosResponse<FavoriteStatusResponse> =
      await this.client.post('/api/user/favorites/add', request)
    return response.data
  }

  async removeFromFavorites(
    request: RemoveFromFavoritesRequest
  ): Promise<FavoriteStatusResponse> {
    const response: AxiosResponse<FavoriteStatusResponse> =
      await this.client.post('/api/user/favorites/remove', request)
    return response.data
  }

  // === Контент ===
  async getCategories(): Promise<CategoriesResponse> {
    const response: AxiosResponse<CategoriesResponse> = await this.client.get(
      '/api/content/categories'
    )
    return response.data
  }

  async getCards(): Promise<CardsResponse> {
    const response: AxiosResponse<CardsResponse> =
      await this.client.get('/api/content/cards')
    return response.data
  }

  async getAnnouncements(): Promise<AnnouncementsResponse> {
    const response: AxiosResponse<AnnouncementsResponse> =
      await this.client.get('/api/content/announcements')
    return response.data
  }

  async getCard(cardId: number): Promise<CardWithContentResponse> {
    const response: AxiosResponse<CardWithContentResponse> =
      await this.client.get(`/api/content/card/${cardId}`)
    return response.data
  }

  async getUsersCount(): Promise<UsersCountResponse> {
    const response: AxiosResponse<UsersCountResponse> = await this.client.get(
      '/api/content/users-count'
    )
    return response.data
  }

  async getSubscriptionPrices(): Promise<SubscriptionPriceListResponse> {
    const response: AxiosResponse<SubscriptionPriceListResponse> =
      await this.client.get('/api/content/subscription/price/list')
    return response.data
  }

  // === Курсы ===
  async getCourses(): Promise<CoursesResponse> {
    const response: AxiosResponse<CoursesResponse> =
      await this.client.get('/api/content/courses')
    return response.data
  }

  async getCourse(courseId: number): Promise<CourseDetailResponse> {
    const response: AxiosResponse<CourseDetailResponse> =
      await this.client.get(`/api/content/course/${courseId}`)
    return response.data
  }

  async getCourseLanding(courseId: number): Promise<CourseDetailResponse> {
    const response: AxiosResponse<CourseDetailResponse> =
      await this.client.get(`/api/content/course-landing/${courseId}`)
    return response.data
  }

  async getLesson(lessonId: number): Promise<LessonDetailResponse> {
    const response: AxiosResponse<LessonDetailResponse> =
      await this.client.get(`/api/content/lesson/${lessonId}`)
    return response.data
  }

  // === Fortune Wheel (Колесо Фортуны) ===
  async canSpin(): Promise<CheckSpinResponse> {
    const response: AxiosResponse<CheckSpinResponse> =
      await this.client.get('/api/spin/can-spin')
    return response.data
  }

  async getSpinPrizes(): Promise<PrizesResponse> {
    const response: AxiosResponse<PrizesResponse> =
      await this.client.get('/api/spin/prizes')
    return response.data
  }

  async getMyPrizes(): Promise<MyPrizesResponse> {
    const response: AxiosResponse<MyPrizesResponse> = await this.client.get(
      '/api/spin/my-prizes'
    )
    return response.data
  }

  async spin(): Promise<SpinResponse> {
    const response: AxiosResponse<SpinResponse> =
      await this.client.post('/api/spin/spin')
    return response.data
  }

  async nextDay(): Promise<CheckSpinResponse> {
    const response: AxiosResponse<CheckSpinResponse> =
      await this.client.post('/api/spin/next_day')
    return response.data
  }

  async resetRewards(): Promise<{ success: boolean; message: string }> {
    const response: AxiosResponse<{ success: boolean; message: string }> =
      await this.client.post('/api/spin/reset_rewards')
    return response.data
  }

  // === Здоровье системы ===
  async healthcheck(): Promise<any> {
    const response = await this.client.get('/api/healthcheck/healthcheck')
    return response.data
  }

  async readiness(): Promise<any> {
    const response = await this.client.get('/api/healthcheck/readiness')
    return response.data
  }
}

// Создаем единственный экземпляр клиента
export const apiClient = new ApiClient()
export default apiClient
